import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../../contexts/AuthContext';
import MarkdownContent from '../common/MarkdownContent';
import AttachmentExplorer from './AttachmentExplorer';
import { getFileUrl } from '../../utils/fileUtil';
import { formatEstimate, parseEstimate } from '../../utils/timeEstimate';

const labelColors = [
  { name: 'Green', color: '#22c55e' },
  { name: 'Yellow', color: '#eab308' },
  { name: 'Orange', color: '#f97316' },
  { name: 'Red', color: '#ef4444' },
  { name: 'Purple', color: '#a855f7' },
  { name: 'Blue', color: '#3b82f6' },
  { name: 'Cyan', color: '#06b6d4' },
];

const CardModal = ({ cardId, onClose, onUpdate, onNavigateToCard, existingLabels = [], existingTags = [] }) => {
  const { token, currentUser } = useContext(AuthContext);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [subtask, setSubtask] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [newLabelText, setNewLabelText] = useState('');
  const [selectedLabelColor, setSelectedLabelColor] = useState(labelColors[0].color);
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [newTagText, setNewTagText] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [previewFile, setPreviewFile] = useState(null); // { url, type: 'image' | 'pdf' }
  const [showMoveOptions, setShowMoveOptions] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [availableBoards, setAvailableBoards] = useState([]);
  const [moveData, setMoveData] = useState({ boardId: '', column: '' });
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editedContactInfo, setEditedContactInfo] = useState({});
  const [isEditingSocial, setIsEditingSocial] = useState(false);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [previewText, setPreviewText] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [activeAttachment, setActiveAttachment] = useState(null);
  const [showFileExplorer, setShowFileExplorer] = useState(false);
  const [showAssigneeList, setShowAssigneeList] = useState(false);
  const [planMsg, setPlanMsg] = useState('');
  const [estimateInput, setEstimateInput] = useState('');
  const [editedSocialMedia, setEditedSocialMedia] = useState({ 
    platforms: [], 
    postCopy: '', 
    platformCopy: { facebook: '', linkedin: '', x: '' }, 
    ctaLink: '' 
  });
  
  // Comment editing state
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskContent, setEditingSubtaskContent] = useState('');
  const [editedCommentContent, setEditedCommentContent] = useState('');
  const [showHistoryId, setShowHistoryId] = useState(null);

  // Parent picker state
  const [linkPickerMode, setLinkPickerMode] = useState(null); // null | 'parent' | 'child'
  const [parentCandidates, setParentCandidates] = useState([]);
  const [parentSearch, setParentSearch] = useState('');

  // Mention state
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionActive, setMentionActive] = useState(false);
  const [atPos, setAtPos] = useState(-1);

  useEffect(() => {
    fetchCardDetails({ propagate: false });
    fetchBoards();
  }, [cardId]);

  const fetchCardDetails = async ({ propagate = true } = {}) => {
    try {
      const res = await axios.get(`/api/cards/${cardId}`, {
        headers: { 'x-auth-token': token }
      });
      setCard(res.data);
      setEstimateInput(res.data.timeEstimate ? formatEstimate(res.data.timeEstimate) : '');
      setEditedDescription(res.data.description || '');
      setEditedTitle(res.data.title || '');
      setEditedContactInfo(res.data.contactInfo || {});
      setEditedSocialMedia(res.data.socialMedia || { platforms: [], postCopy: '', platformCopy: { facebook: '', linkedin: '', x: '' }, ctaLink: '' });
      setShowAssigneeList(false);
      setLoading(false);
      if (propagate) onUpdate?.();
    } catch (err) {
      console.error(err);
      onClose();
    }
  };

  const handleUpdateContact = async () => {
    try {
      await axios.put(`/api/cards/${cardId}`, { contactInfo: editedContactInfo }, {
        headers: { 'x-auth-token': token }
      });
      setIsEditingContact(false);
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateSocial = async () => {
    try {
      await axios.put(`/api/cards/${cardId}`, { socialMedia: editedSocialMedia }, {
        headers: { 'x-auth-token': token }
      });
      setIsEditingSocial(false);
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBoards = async () => {
    try {
      const res = await axios.get('/api/boards', {
        headers: { 'x-auth-token': token }
      });
      setAvailableBoards(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getCollaborators = () => {
    if (!card || !card.board) return [];
    const board = card.board;
    const collaborators = new Map();

    if (board.owner) collaborators.set(board.owner._id, board.owner);
    if (board.members) {
      board.members.forEach(m => {
        if (m.user) collaborators.set(m.user._id, m.user);
      });
    }
    if (board.workspace && board.workspace.members) {
      board.workspace.members.forEach(m => {
        if (m.user) collaborators.set(m.user._id, m.user);
      });
    }
    collaborators.delete(currentUser?._id);
    return Array.from(collaborators.values());
  };

  useEffect(() => {
    if (mentionActive) {
      const collaborators = getCollaborators();
      const filtered = collaborators.filter(c => 
        (c.name && c.name.toLowerCase().includes(mentionSearch.toLowerCase())) ||
        (c.email && c.email.toLowerCase().includes(mentionSearch.toLowerCase()))
      );
      setMentionSuggestions(filtered.slice(0, 8));
      if (mentionIndex >= filtered.length) setMentionIndex(0);
    } else {
      setMentionSuggestions([]);
    }
  }, [mentionActive, mentionSearch, card]);

  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    setComment(value);

    const lastAtPos = value.lastIndexOf('@', cursorPosition - 1);
    if (lastAtPos !== -1) {
      const query = value.substring(lastAtPos + 1, cursorPosition);
      const charBeforeAt = lastAtPos > 0 ? value[lastAtPos - 1] : ' ';
      
      if ((charBeforeAt === ' ' || charBeforeAt === '\n') && !query.includes(' ')) {
        setMentionActive(true);
        setMentionSearch(query);
        setAtPos(lastAtPos);
      } else {
        setMentionActive(false);
      }
    } else {
      setMentionActive(false);
    }
  };

  const insertMention = (user) => {
    const before = comment.substring(0, atPos);
    const after = comment.substring(atPos + mentionSearch.length + 1);
    const mentionString = `[@${user.name}](${user._id})`;
    setComment(before + mentionString + ' ' + after);
    setMentionActive(false);
    setMentionSearch('');
  };

  const handleKeyDown = (e) => {
    if (mentionActive && mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % mentionSuggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((prev) => (prev - 1 + mentionSuggestions.length) % mentionSuggestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionSuggestions[mentionIndex]);
      } else if (e.key === 'Escape') {
        setMentionActive(false);
      }
    }
  };

  const handleUpdateDescription = async () => {
    try {
      await axios.put(`/api/cards/${cardId}`, { description: editedDescription }, {
        headers: { 'x-auth-token': token }
      });
      setIsEditingDescription(false);
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateTitle = async () => {
    if (!editedTitle.trim() || editedTitle === card.title) {
      setIsEditingTitle(false);
      return;
    }
    try {
      await axios.put(`/api/cards/${cardId}`, { title: editedTitle }, {
        headers: { 'x-auth-token': token }
      });
      setIsEditingTitle(false);
      fetchCardDetails();
      onUpdate(); // Refresh the board view
    } catch (err) {
      console.error(err);
    }
  };

  // Drop this card into today's first free Daily Plan slot.
  const handleAddToDailyPlan = async () => {
    const now = new Date();
    const dateKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    setPlanMsg('Adding…');
    try {
      const res = await axios.post(`/api/dayplan/${dateKey}/add-card`, { cardId }, {
        headers: { 'x-auth-token': token }
      });
      if (res.data.added) setPlanMsg('Added to today ✓');
      else setPlanMsg(res.data.msg || 'Already on today');
    } catch (err) {
      setPlanMsg(err.response?.data?.msg || 'Could not add');
    }
    setTimeout(() => setPlanMsg(''), 3000);
  };

  const handleArchiveCard = async () => {
    try {
      await axios.put(`/api/cards/${cardId}`, { isArchived: true }, {
        headers: { 'x-auth-token': token }
      });
      onUpdate();
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteCardPermanent = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this card? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/cards/${cardId}`, {
        headers: { 'x-auth-token': token }
      });
      onUpdate();
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    // Check if an agent was mentioned
    const hasAgentMention = comment.includes('🤖') || comment.includes('Agent'); // Simple heuristic or we could be more precise
    
    try {
      const res = await axios.post(`/api/cards/${cardId}/comments`, { content: comment }, {
        headers: { 'x-auth-token': token }
      });
      setCard(res.data);
      setComment('');

      if (hasAgentMention) {
        setIsProcessingAI(true);
        // Poll for update after 3 seconds
        setTimeout(async () => {
          await fetchCardDetails();
          setIsProcessingAI(false);
        }, 3500);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateComment = async (commentId) => {
    try {
      const res = await axios.put(`/api/cards/${cardId}/comments/${commentId}`, { content: editedCommentContent }, {
        headers: { 'x-auth-token': token }
      });
      setCard(res.data);
      setEditingCommentId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const res = await axios.delete(`/api/cards/${cardId}/comments/${commentId}`, {
        headers: { 'x-auth-token': token }
      });
      setCard(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddSubtask = async (e) => {
    e.preventDefault();
    if (!subtask.trim()) return;
    try {
      await axios.post(`/api/cards/${cardId}/subtasks`, { content: subtask }, {
        headers: { 'x-auth-token': token }
      });
      setSubtask('');
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleSubtask = async (subtaskId) => {
    try {
      await axios.put(`/api/cards/${cardId}/subtasks/${subtaskId}/toggle`, {}, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const startEditSubtask = (st) => {
    setEditingSubtaskId(st._id);
    setEditingSubtaskContent(st.content);
  };

  const cancelEditSubtask = () => {
    setEditingSubtaskId(null);
    setEditingSubtaskContent('');
  };

  const saveSubtask = async (subtaskId) => {
    const next = editingSubtaskContent.trim();
    if (!next) return cancelEditSubtask();
    try {
      await axios.put(
        `/api/cards/${cardId}/subtasks/${subtaskId}`,
        { content: next },
        { headers: { 'x-auth-token': token } }
      );
      cancelEditSubtask();
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteSubtask = async (subtaskId) => {
    if (!window.confirm('Delete this subtask?')) return;
    try {
      await axios.delete(`/api/cards/${cardId}/subtasks/${subtaskId}`, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  // Straight to S3 with a presigned PUT, so big files (up to 1 GB) never pass
  // through the app's proxies. Returns null when the server has no S3, and the
  // caller falls back to the multipart route.
  const uploadDirect = async (file) => {
    let data;
    try {
      ({ data } = await axios.post(`/api/cards/${cardId}/attachments/upload-url`, {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size
      }, { headers: { 'x-auth-token': token } }));
    } catch (err) {
      if (err.response?.data?.directUpload === false) return null;
      throw err;
    }
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', data.uploadUrl);
      xhr.setRequestHeader('Content-Type', data.contentType);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) setUploadPercent(Math.round((evt.loaded / evt.total) * 100));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`)));
      xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
      xhr.send(file);
    });
    return axios.post(`/api/cards/${cardId}/attachments/direct`,
      { key: data.key, name: file.name, contentType: data.contentType },
      { headers: { 'x-auth-token': token } });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    setUploadPercent(0);
    try {
      let res = await uploadDirect(file);
      if (!res) {
        const formData = new FormData();
        formData.append('file', file);
        res = await axios.post(`/api/cards/${cardId}/attachments`, formData, {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      setCard(res.data);
    } catch (err) {
      console.error('Attachment upload error:', err);
      alert(`Error uploading file: ${err.response?.data?.msg || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSetCover = async (attachmentPath) => {
    try {
      await axios.put(`/api/cards/${cardId}`, { coverPhoto: attachmentPath }, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDueDateChange = async (date) => {
    try {
      await axios.put(`/api/cards/${cardId}`, { dueDate: date }, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEstimateChange = async (minutes) => {
    try {
      await axios.put(`/api/cards/${cardId}`, { timeEstimate: minutes }, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const commitEstimateInput = () => {
    const trimmed = estimateInput.trim();
    if (!trimmed) {
      if (card?.timeEstimate) handleEstimateChange(null);
      return;
    }
    const minutes = parseEstimate(trimmed);
    if (minutes !== null && minutes !== card?.timeEstimate) {
      handleEstimateChange(minutes);
    } else if (minutes === null) {
      // Unparseable — snap the field back to the stored value.
      setEstimateInput(card?.timeEstimate ? formatEstimate(card.timeEstimate) : '');
    }
  };

  const handleRecurrenceChange = async (next) => {
    try {
      await axios.put(`/api/cards/${cardId}`, { recurrence: next }, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error('Recurrence update failed:', err);
    }
  };

  const handleSetParent = async (parentId) => {
    try {
      await axios.put(`/api/cards/${cardId}`, { parent: parentId }, {
        headers: { 'x-auth-token': token }
      });
      setLinkPickerMode(null);
      setParentSearch('');
      fetchCardDetails();
    } catch (err) {
      console.error('Set parent failed:', err);
    }
  };

  // Parent-side linking: make an existing card a child of THIS card.
  const handleAddChild = async (childId) => {
    try {
      await axios.put(`/api/cards/${childId}`, { parent: cardId }, {
        headers: { 'x-auth-token': token }
      });
      setLinkPickerMode(null);
      setParentSearch('');
      fetchCardDetails();
    } catch (err) {
      console.error('Add child failed:', err);
    }
  };

  const openLinkPicker = async (mode) => {
    setLinkPickerMode(mode);
    setParentSearch('');
    if (card?.board?._id) {
      try {
        const res = await axios.get(`/api/cards/board/${card.board._id}`, {
          headers: { 'x-auth-token': token }
        });
        setParentCandidates(res.data.filter(c => c._id !== cardId));
      } catch (err) {
        console.error('Failed to load link candidates:', err);
      }
    }
  };

  const handlePromoteSubtasks = async () => {
    if (!window.confirm(`Promote ${card.subtasks?.length || 0} subtask(s) to individual cards on the board? This cannot be undone.`)) return;
    try {
      await axios.post(`/api/cards/${cardId}/promote-subtasks`, {}, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error('Promote subtasks failed:', err);
      alert(err.response?.data?.msg || 'Failed to promote subtasks');
    }
  };

  const toggleAssignee = async (userId) => {
    try {
      const currentAssignees = card.assignedTo || [];
      // Current assignees might be populated objects or just IDs
      const isAssigned = currentAssignees.some(u => (u._id || u) === userId);
      
      let newAssigneeIds;
      if (isAssigned) {
        newAssigneeIds = currentAssignees
          .filter(u => (u._id || u) !== userId)
          .map(u => u._id || u);
      } else {
        newAssigneeIds = [...currentAssignees.map(u => u._id || u), userId];
      }

      await axios.put(`/api/cards/${cardId}`, { assignedTo: newAssigneeIds }, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error('Error toggling assignee:', err);
    }
  };

  const handleAddLabel = async () => {
    if (!newLabelText.trim()) return;
    const updatedLabels = [...(card.labels || []), { text: newLabelText, color: selectedLabelColor }];
    try {
      await axios.put(`/api/cards/${cardId}`, { labels: updatedLabels }, {
        headers: { 'x-auth-token': token }
      });
      setNewLabelText('');
      setShowLabelInput(false);
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const quickAddLabel = async (label) => {
    const updatedLabels = [...(card.labels || []), { text: label.text, color: label.color }];
    try {
      await axios.put(`/api/cards/${cardId}`, { labels: updatedLabels }, {
        headers: { 'x-auth-token': token }
      });
      setShowLabelInput(false);
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const removeLabel = async (labelId) => {
    const updatedLabels = card.labels.filter(l => l._id !== labelId);
    try {
      await axios.put(`/api/cards/${cardId}`, { labels: updatedLabels }, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const saveTags = async (updatedTags) => {
    try {
      await axios.put(`/api/cards/${cardId}`, { tags: updatedTags }, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddTag = async () => {
    const text = newTagText.trim();
    if (!text) return;
    await saveTags([...(card.tags || []), text]);
    setNewTagText('');
  };

  const removeTag = async (tag) => {
    await saveTags((card.tags || []).filter(t => t !== tag));
  };

  // Publishes this card as an unlisted page (brief + files) for people outside
  // the tracker — a design vendor, a printer. Anyone with the link can read it.
  const shareUrl = card?.share?.token ? `${window.location.origin}/r/${card.share.token}` : '';

  const handleCreateShare = async () => {
    setShareBusy(true);
    try {
      const res = await axios.post(`/api/cards/${cardId}/share`, {}, {
        headers: { 'x-auth-token': token }
      });
      const url = `${window.location.origin}/r/${res.data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      } catch (e) { /* clipboard blocked; the link is shown on screen */ }
      fetchCardDetails();
    } catch (err) {
      console.error('Share failed:', err);
    } finally {
      setShareBusy(false);
    }
  };

  const handleRevokeShare = async () => {
    if (!window.confirm('Turn off the public link? Anyone you already sent it to will lose access.')) return;
    setShareBusy(true);
    try {
      await axios.delete(`/api/cards/${cardId}/share`, { headers: { 'x-auth-token': token } });
      fetchCardDetails();
    } catch (err) {
      console.error('Revoke failed:', err);
    } finally {
      setShareBusy(false);
    }
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch (e) { /* clipboard blocked */ }
  };

  const handleMoveCard = async () => {
    if (!moveData.boardId || !moveData.column) return;
    try {
      await axios.put(`/api/cards/${cardId}`, moveData, {
        headers: { 'x-auth-token': token }
      });
      onUpdate(); // Refresh the board
      onClose();  // Close modal since it's on a different board now
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await axios.delete(`/api/cards/${cardId}/attachments/${attachmentId}`, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const handlePreviewAttachment = async (attachmentId) => {
    try {
      const res = await axios.get(`/api/cards/${cardId}/attachments/${attachmentId}/text`, {
        headers: { 'x-auth-token': token }
      });
      setPreviewText(res.data.text);
      setIsPreviewModalOpen(true);
    } catch (err) {
      alert('Failed to extract text from this document.');
    }
  };

  const handleOpenPreview = (e, attachment) => {
    if (e) e.preventDefault();
    setActiveAttachment(attachment);
    const url = attachment.signedUrl || getFileUrl(attachment.path);
    
    if (isImage(attachment.path)) {
      setPreviewFile({ url: url, type: 'image' });
    } else if (isPdf(attachment.path)) {
      setPreviewFile({ url: url, type: 'pdf' });
    } else if (attachment.name.toLowerCase().endsWith('.docx') || attachment.name.toLowerCase().endsWith('.txt') || attachment.name.toLowerCase().endsWith('.md')) {
      handlePreviewAttachment(attachment._id);
    } else {
      // Just open in new tab if not previewable
      window.open(url, '_blank');
    }
  };

  const handleReviewAttachment = async (attachment) => {
    const agentName = prompt('Which agent should review this? (Enter name or mention)');
    if (!agentName) return;
    
    setComment(`@${agentName} please review the attached document "${attachment.name}" and provide feedback.`);
  };

  const isImage = (path) => /\.(jpg|jpeg|png|gif|webp)$/i.test(path);
  const isPdf = (path) => /\.pdf$/i.test(path);

  const handlePreview = (e, url) => {
    if (isImage(url)) {
      e.preventDefault();
      setPreviewFile({ url: url, type: 'image' });
    } else if (isPdf(url)) {
      e.preventDefault();
      setPreviewFile({ url: url, type: 'pdf' });
    }
  };

  const handleSubscription = async () => {
    const isSubscribed = card.subscribers.some(s => {
      const id = typeof s === 'string' ? s : s._id;
      return id === currentUser?._id;
    });
    const endpoint = isSubscribed ? 'unsubscribe' : 'subscribe';
    try {
      await axios.post(`/api/cards/${cardId}/${endpoint}`, {}, {
        headers: { 'x-auth-token': token }
      });
      fetchCardDetails();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return null;

  const isSubscribed = card.subscribers.some(s => {
      const id = typeof s === 'string' ? s : s._id;
      return id === currentUser?._id;
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/60 backdrop-blur-[3px] transition-all" onClick={onClose}></div>
        <div className="relative bg-white dark:bg-[#0e1321] border border-gray-150 dark:border-gray-800/80 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto z-10 transition-all duration-300">
          {/* Cover Photo */}
          {card.coverPhoto && (
            <div className="h-36 w-full bg-gray-100 dark:bg-gray-900/30 relative group">
              <img 
                src={getFileUrl(card.coverPhoto)} 
                alt="Cover" 
                className="w-full h-full object-cover rounded-t-2xl"
              />
              <button 
                onClick={() => handleSetCover('')}
                className="absolute bottom-3 right-3 bg-gray-900/80 hover:bg-gray-900 text-white text-xs px-3 py-1.5 rounded-xl opacity-0 group-hover:opacity-100 transition-all shadow-md"
              >
                Remove Cover
              </button>
            </div>
          )}

          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1 min-w-0 mr-4">
                {isEditingTitle ? (
                  <div className="flex items-center space-x-2">
                    <input
                      autoFocus
                      type="text"
                      className="text-2xl font-bold text-gray-900 dark:text-gray-100 border-b-2 border-primary-blue focus:outline-none w-full bg-transparent"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateTitle();
                        if (e.key === 'Escape') {
                          setIsEditingTitle(false);
                          setEditedTitle(card.title);
                        }
                      }}
                    />
                    <button 
                      onClick={handleUpdateTitle}
                      className="p-1 text-green-600 hover:bg-green-50 rounded"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                    </button>
                    <button 
                      onClick={() => {
                        setIsEditingTitle(false);
                        setEditedTitle(card.title);
                      }}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ) : (
                  <h2 
                    className="text-2xl font-bold text-gray-900 dark:text-gray-100 cursor-pointer hover:text-primary-blue transition-colors group flex items-center"
                    onClick={() => setIsEditingTitle(true)}
                  >
                    {card.title}
                    <svg className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  </h2>
                )}
                {card.completedAt && (
                  <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    Completed {new Date(card.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
                {/* Labels Display */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {card.labels?.map(label => (
                    <span 
                      key={label._id} 
                      className="px-2 py-0.5 rounded text-[10px] font-bold text-white flex items-center group relative"
                      style={{ backgroundColor: label.color }}
                    >
                      {label.text}
                      <button 
                        onClick={() => removeLabel(label._id)}
                        className="ml-1 hover:text-gray-200"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                      </button>
                    </span>
                  ))}
                  <button 
                    onClick={() => setShowLabelInput(!showLabelInput)}
                    className="px-3 py-1 rounded-full text-[10px] font-bold bg-gray-100 dark:bg-gray-800 text-gray-650 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200/50 dark:border-gray-700/50 transition-all"
                  >
                    + Add Label
                  </button>
                </div>
                {showLabelInput && (
                  <div className="mt-2.5 p-3 bg-gray-50 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-850 shadow-md">
                    {(() => {
                      const onCard = new Set((card.labels || []).map(l => l.text));
                      const picks = existingLabels.filter(l => l && l.text && !onCard.has(l.text));
                      if (!picks.length) return null;
                      return (
                        <div className="mb-2.5">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">Quick pick</p>
                          <div className="flex flex-wrap gap-1.5">
                            {picks.map(l => (
                              <button
                                key={l.text}
                                onClick={() => quickAddLabel(l)}
                                className="px-3 py-1 rounded-full text-[10px] font-bold text-white shadow-sm hover:opacity-80 transition-opacity"
                                style={{ backgroundColor: l.color }}
                              >
                                {l.text}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <input
                      type="text" 
                      placeholder="Label text..."
                      value={newLabelText}
                      onChange={(e) => setNewLabelText(e.target.value)}
                      className="w-full text-xs border-gray-300 dark:border-gray-700 rounded-lg focus:ring-primary-blue focus:border-primary-blue mb-2.5 p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                    <div className="flex flex-wrap gap-1.5 mb-2.5">
                      {labelColors.map(lc => (
                        <button 
                          key={lc.color}
                          onClick={() => setSelectedLabelColor(lc.color)}
                          className={`w-6 h-6 rounded-md ${selectedLabelColor === lc.color ? 'ring-2 ring-offset-1 ring-primary-blue' : ''}`}
                          style={{ backgroundColor: lc.color }}
                          title={lc.name}
                        />
                      ))}
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button onClick={() => setShowLabelInput(false)} className="text-xs text-gray-500 dark:text-gray-400 hover:underline px-2 py-1">Cancel</button>
                      <button onClick={handleAddLabel} className="text-xs bg-primary-blue text-white px-3 py-1 rounded-xl hover:bg-blue-700 shadow-sm font-bold">Add</button>
                    </div>
                  </div>
                )}
                {/* Tags Display (freeform, separate from colored labels) */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {card.tags?.map(tag => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center border border-gray-300/50 dark:border-gray-600/50"
                    >
                      #{tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:text-gray-900 dark:hover:text-white"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                      </button>
                    </span>
                  ))}
                  <button
                    onClick={() => setShowTagInput(!showTagInput)}
                    className="px-3 py-1 rounded-full text-[10px] font-bold bg-gray-100 dark:bg-gray-800 text-gray-650 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200/50 dark:border-gray-700/50 transition-all"
                  >
                    + Add Tag
                  </button>
                </div>
                {showTagInput && (
                  <div className="mt-2.5 p-3 bg-gray-50 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-850 shadow-md">
                    {(() => {
                      const onCard = new Set((card.tags || []).map(t => t.toLowerCase()));
                      const picks = existingTags.filter(t => t && !onCard.has(t.toLowerCase()));
                      if (!picks.length) return null;
                      return (
                        <div className="mb-2.5">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1.5">Quick pick</p>
                          <div className="flex flex-wrap gap-1.5">
                            {picks.map(t => (
                              <button
                                key={t}
                                onClick={() => saveTags([...(card.tags || []), t])}
                                className="px-3 py-1 rounded-full text-[10px] font-bold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 shadow-sm hover:opacity-80 transition-opacity"
                              >
                                #{t}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <input
                      type="text"
                      placeholder="Tag name..."
                      value={newTagText}
                      onChange={(e) => setNewTagText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddTag(); }}
                      className="w-full text-xs border-gray-300 dark:border-gray-700 rounded-lg focus:ring-primary-blue focus:border-primary-blue mb-2.5 p-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                    <div className="flex justify-end space-x-2">
                      <button onClick={() => setShowTagInput(false)} className="text-xs text-gray-500 dark:text-gray-400 hover:underline px-2 py-1">Cancel</button>
                      <button onClick={handleAddTag} className="text-xs bg-primary-blue text-white px-3 py-1 rounded-xl hover:bg-blue-700 shadow-sm font-bold">Add</button>
                    </div>
                  </div>
                )}
              </div>
              <button 
                onClick={onClose} 
                className="p-1.5 text-gray-400 dark:text-gray-550 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all ml-4"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-6">
                {/* Description */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" /></svg>
                      Description
                    </h3>
                    {!isEditingDescription && (
                      <button 
                        onClick={() => setIsEditingDescription(true)}
                        className="text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-650 dark:text-gray-300 px-3 py-1 rounded-xl transition-all border border-gray-200/50 dark:border-gray-700/50"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  
                  {isEditingDescription ? (
                    <div className="space-y-2.5">
                      <textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        className="w-full text-sm border-gray-300 dark:border-gray-750 rounded-xl focus:ring-primary-blue focus:border-primary-blue p-3 min-h-[200px] bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Add a detailed description..."
                      />
                      <div className="flex space-x-2">
                        <button 
                          onClick={handleUpdateDescription}
                          className="px-4 py-2 bg-primary-blue text-white text-xs font-bold rounded-xl hover:bg-blue-700 shadow-md transition-all"
                        >
                          Save
                        </button>
                        <button 
                          onClick={() => {
                            setIsEditingDescription(false);
                            setEditedDescription(card.description || '');
                          }}
                          className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all border border-gray-200/50 dark:border-gray-700/50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <MarkdownContent 
                      content={card.description || '*No description provided.*'} 
                      className="text-gray-700"
                    />
                  )}
                </div>

                {/* Contact Info (Special for Companies/Persons) */}
                {(card.cardType === 'company' || card.cardType === 'person') && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] flex items-center">
                        {card.cardType === 'company' ? '🏢 Company Information' : '👤 Person Information'}
                      </h3>
                      {!isEditingContact ? (
                        <button 
                          onClick={() => setIsEditingContact(true)}
                          className="text-[10px] bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded font-bold"
                        >
                          Edit Details
                        </button>
                      ) : (
                        <div className="flex space-x-2">
                          <button onClick={handleUpdateContact} className="text-[10px] bg-primary-blue text-white px-2 py-1 rounded font-bold">Save</button>
                          <button onClick={() => setIsEditingContact(false)} className="text-[10px] bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-1 rounded font-bold">Cancel</button>
                        </div>
                      )}
                    </div>

                    {isEditingContact ? (
                      <div className="grid grid-cols-2 gap-4 animate-fade-in">
                        {card.cardType === 'person' && (
                          <>
                            <div>
                              <label className="block text-[8px] font-black text-gray-400 uppercase mb-1">First Name</label>
                              <input 
                                type="text" 
                                className="w-full text-xs border-gray-200 dark:border-gray-600 rounded p-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                value={editedContactInfo.firstName || ''}
                                onChange={(e) => setEditedContactInfo({ ...editedContactInfo, firstName: e.target.value })}
                              />
                            </div>
                            <div>
                              <label className="block text-[8px] font-black text-gray-400 uppercase mb-1">Last Name</label>
                              <input 
                                type="text" 
                                className="w-full text-xs border-gray-200 dark:border-gray-600 rounded p-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                value={editedContactInfo.lastName || ''}
                                onChange={(e) => setEditedContactInfo({ ...editedContactInfo, lastName: e.target.value })}
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-[8px] font-black text-gray-400 uppercase mb-1">Job Title</label>
                              <input 
                                type="text" 
                                className="w-full text-xs border-gray-200 dark:border-gray-600 rounded p-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                value={editedContactInfo.jobTitle || ''}
                                onChange={(e) => setEditedContactInfo({ ...editedContactInfo, jobTitle: e.target.value })}
                              />
                            </div>
                          </>
                        )}
                        
                        <div className={card.cardType === 'company' ? 'col-span-2' : ''}>
                          <label className="block text-[8px] font-black text-gray-400 uppercase mb-1">{card.cardType === 'company' ? 'Full Entity Name' : 'Company Name'}</label>
                          <input 
                            type="text" 
                            className="w-full text-xs border-gray-200 rounded p-1.5"
                            value={editedContactInfo.companyName || ''}
                            onChange={(e) => setEditedContactInfo({ ...editedContactInfo, companyName: e.target.value })}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-[8px] font-black text-gray-400 uppercase mb-1">Email</label>
                          <input 
                            type="email" 
                            className="w-full text-xs border-gray-200 rounded p-1.5"
                            value={editedContactInfo.email || ''}
                            onChange={(e) => setEditedContactInfo({ ...editedContactInfo, email: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-[8px] font-black text-gray-400 uppercase mb-1">Phone</label>
                          <input 
                            type="text" 
                            className="w-full text-xs border-gray-200 rounded p-1.5"
                            value={editedContactInfo.phone || ''}
                            onChange={(e) => setEditedContactInfo({ ...editedContactInfo, phone: e.target.value })}
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <label className="block text-[8px] font-black text-gray-400 uppercase mb-1">Website</label>
                          <input 
                            type="text" 
                            className="w-full text-xs border-gray-200 rounded p-1.5"
                            value={editedContactInfo.website || ''}
                            onChange={(e) => setEditedContactInfo({ ...editedContactInfo, website: e.target.value })}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-[8px] font-black text-gray-400 uppercase mb-1">Address</label>
                          <textarea 
                            className="w-full text-xs border-gray-200 rounded p-1.5"
                            rows="2"
                            value={editedContactInfo.address || ''}
                            onChange={(e) => setEditedContactInfo({ ...editedContactInfo, address: e.target.value })}
                          ></textarea>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                        {card.cardType === 'person' && (
                          <>
                            <div className="col-span-2 flex items-center space-x-3 border-b border-gray-100 dark:border-gray-700 pb-3">
                              <div className="h-10 w-10 bg-primary-blue text-white font-black rounded-full flex items-center justify-center uppercase">
                                {card.contactInfo?.firstName?.charAt(0) || card.title?.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-black text-gray-900 dark:text-gray-100 uppercase tracking-tighter">
                                  {card.contactInfo?.firstName} {card.contactInfo?.lastName}
                                </p>
                                <p className="text-[10px] font-bold text-gray-400 uppercase">
                                  {card.contactInfo?.jobTitle || 'Person'}
                                </p>
                              </div>
                            </div>
                            {card.contactInfo?.companyName && (
                              <div>
                                <p className="text-[8px] font-black text-gray-400 uppercase mb-1">Company</p>
                                <p className="text-xs font-bold text-gray-800">{card.contactInfo.companyName}</p>
                              </div>
                            )}
                          </>
                        )}
                        
                        {card.cardType === 'company' && (
                           <div className="col-span-2">
                             <p className="text-[8px] font-black text-gray-400 uppercase mb-1">Company Name</p>
                             <p className="text-sm font-black text-gray-800 uppercase tracking-tighter">{card.contactInfo?.companyName || card.title}</p>
                           </div>
                        )}

                        {card.contactInfo?.email && (
                          <div>
                            <p className="text-[8px] font-black text-gray-400 uppercase mb-1">Email</p>
                            <a href={`mailto:${card.contactInfo.email}`} className="text-xs font-bold text-primary-blue hover:underline">{card.contactInfo.email}</a>
                          </div>
                        )}
                        {card.contactInfo?.phone && (
                          <div>
                            <p className="text-[8px] font-black text-gray-400 uppercase mb-1">Phone</p>
                            <a href={`tel:${card.contactInfo.phone}`} className="text-xs font-bold text-gray-800 hover:text-primary-blue">{card.contactInfo.phone}</a>
                          </div>
                        )}
                        {card.contactInfo?.website && (
                          <div className="col-span-2">
                            <p className="text-[8px] font-black text-gray-400 uppercase mb-1">Website</p>
                            <a href={card.contactInfo.website.startsWith('http') ? card.contactInfo.website : `https://${card.contactInfo.website}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-primary-blue hover:underline">{card.contactInfo.website}</a>
                          </div>
                        )}
                        {card.contactInfo?.address && (
                          <div className="col-span-2">
                            <p className="text-[8px] font-black text-gray-400 uppercase mb-1">Address</p>
                            <p className="text-xs font-medium text-gray-600 whitespace-pre-wrap">{card.contactInfo.address}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Social Media Details (Special for Social Posts) */}
                {card.cardType === 'social' && (
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xs font-black text-blue-400 uppercase tracking-[0.2em] flex items-center">
                        📢 Social Media Content
                      </h3>
                      {!isEditingSocial ? (
                        <button 
                          onClick={() => setIsEditingSocial(true)}
                          className="text-[10px] bg-white dark:bg-gray-700 border border-blue-100 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-gray-600 text-blue-600 dark:text-blue-400 px-2 py-1 rounded font-bold"
                        >
                          Edit Content
                        </button>
                      ) : (
                        <div className="flex space-x-2">
                          <button onClick={handleUpdateSocial} className="text-[10px] bg-primary-blue text-white px-2 py-1 rounded font-bold">Save</button>
                          <button onClick={() => setIsEditingSocial(false)} className="text-[10px] bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-1 rounded font-bold">Cancel</button>
                        </div>
                      )}
                    </div>

                    {isEditingSocial ? (
                      <div className="space-y-4 animate-fade-in">
                        <div>
                          <label className="block text-[8px] font-black text-blue-400 uppercase mb-2">Target Platforms</label>
                          <div className="flex space-x-2">
                            {['Facebook', 'LinkedIn', 'X'].map(p => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => {
                                  const platforms = editedSocialMedia.platforms.includes(p)
                                    ? editedSocialMedia.platforms.filter(plat => plat !== p)
                                    : [...editedSocialMedia.platforms, p];
                                  setEditedSocialMedia({ ...editedSocialMedia, platforms });
                                }}
                                className={`px-2 py-1 text-[10px] font-bold rounded border transition-all ${editedSocialMedia.platforms.includes(p) ? 'bg-primary-blue text-white border-primary-blue' : 'bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600'}`}
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-[8px] font-black text-blue-400 uppercase mb-1">General Notes / Default Copy</label>
                          <textarea 
                            className="w-full text-xs border-blue-100 rounded p-1.5 focus:ring-primary-blue"
                            rows="2"
                            value={editedSocialMedia.postCopy || ''}
                            onChange={(e) => setEditedSocialMedia({ ...editedSocialMedia, postCopy: e.target.value })}
                          ></textarea>
                        </div>

                        {editedSocialMedia.platforms.map(p => (
                          <div key={p} className="animate-fade-in">
                            <label className="block text-[8px] font-black text-blue-400 uppercase mb-1">{p} Specific Copy</label>
                            <textarea 
                              className="w-full text-xs border-blue-100 dark:border-blue-900 rounded p-1.5 focus:ring-primary-blue bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              rows="3"
                              value={editedSocialMedia.platformCopy?.[p.toLowerCase()] || ''}
                              onChange={(e) => setEditedSocialMedia({ 
                                ...editedSocialMedia, 
                                platformCopy: { ...editedSocialMedia.platformCopy, [p.toLowerCase()]: e.target.value } 
                              })}
                            ></textarea>
                          </div>
                        ))}

                        <div>
                          <label className="block text-[8px] font-black text-blue-400 uppercase mb-1">CTA Link</label>
                          <input 
                            type="text" 
                            className="w-full text-xs border-blue-100 rounded p-1.5 focus:ring-primary-blue"
                            value={editedSocialMedia.ctaLink || ''}
                            onChange={(e) => setEditedSocialMedia({ ...editedSocialMedia, ctaLink: e.target.value })}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                          {card.socialMedia?.platforms?.length > 0 ? (
                            card.socialMedia.platforms.map(p => (
                              <span key={p} className="px-2 py-1 bg-white dark:bg-gray-700 border border-blue-100 dark:border-blue-900 text-[10px] font-black text-blue-600 dark:text-blue-400 rounded uppercase tracking-tighter">
                                {p}
                              </span>
                            ))
                          ) : (
                            <span className="text-[10px] text-gray-400 italic">No platforms selected</span>
                          )}
                        </div>
                        
                        {card.socialMedia?.platformCopy && Object.entries(card.socialMedia.platformCopy).map(([platform, copy]) => (
                          copy && (
                            <div key={platform} className="bg-white dark:bg-gray-700 p-3 rounded-lg border border-blue-50 dark:border-blue-900">
                              <p className="text-[8px] font-black text-blue-400 uppercase mb-2 tracking-widest">{platform} Copy</p>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{copy}</p>
                            </div>
                          )
                        ))}

                        {card.socialMedia?.postCopy && (
                          <div className="bg-white p-3 rounded-lg border border-blue-50">
                            <p className="text-[8px] font-black text-gray-400 uppercase mb-2 tracking-widest">General Notes</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{card.socialMedia.postCopy}</p>
                          </div>
                        )}

                        {card.socialMedia?.ctaLink && (
                          <div>
                            <p className="text-[8px] font-black text-gray-400 uppercase mb-1">Call to Action Link</p>
                            <a 
                              href={card.socialMedia.ctaLink.startsWith('http') ? card.socialMedia.ctaLink : `https://${card.socialMedia.ctaLink}`} 
                              target="_blank" 
                              rel="noreferrer" 
                              className="text-xs font-bold text-primary-blue hover:underline break-all"
                            >
                              {card.socialMedia.ctaLink}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Linked cards (parent + children) */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                      Linked Cards
                    </span>
                    <span className="flex items-center gap-1.5 normal-case tracking-normal">
                      {!card.parent && (
                        <button
                          onClick={() => openLinkPicker('parent')}
                          className="text-[10px] px-3 py-1 bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 rounded-xl hover:bg-blue-200 dark:hover:bg-blue-900/40 font-bold border border-blue-200/20"
                          title="Link this card under a parent card (e.g. a project)"
                        >
                          Set Parent
                        </button>
                      )}
                      <button
                        onClick={() => openLinkPicker('child')}
                        className="text-[10px] px-3 py-1 bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 rounded-xl hover:bg-blue-200 dark:hover:bg-blue-900/40 font-bold border border-blue-200/20"
                        title="Link an existing card as a child of this card"
                      >
                        Add Child
                      </button>
                    </span>
                  </h3>

                  {card.parent && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 ml-6 mb-2">
                      <span className="font-semibold">Child of:</span>
                      <button
                        onClick={() => onNavigateToCard && onNavigateToCard(card.parent._id || card.parent)}
                        className="text-primary-blue font-medium hover:underline"
                      >
                        ↳ {card.parent.title || 'parent card'}
                      </button>
                      <button
                        onClick={() => handleSetParent(null)}
                        className="text-[10px] text-gray-400 hover:text-red-500 ml-auto"
                        title="Remove parent"
                      >
                        remove
                      </button>
                    </div>
                  )}

                  {linkPickerMode && (
                    <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                        {linkPickerMode === 'parent' ? 'Pick a parent card' : 'Pick a card to link as a child'}
                      </p>
                      <input
                        type="text"
                        autoFocus
                        value={parentSearch}
                        onChange={(e) => setParentSearch(e.target.value)}
                        placeholder="Search cards..."
                        className="w-full text-xs border-gray-300 rounded mb-2 focus:ring-primary-blue focus:border-primary-blue"
                      />
                      <div className="max-h-48 overflow-y-auto space-y-0.5">
                        {parentCandidates
                          .filter(c => {
                            const childIds = (card.children || []).map(ch => ch._id);
                            const parentId = card.parent && (card.parent._id || card.parent);
                            // Keep the tree one level deep: no linking to your own child/parent.
                            if (childIds.includes(c._id) || c._id === parentId) return false;
                            return !parentSearch || c.title.toLowerCase().includes(parentSearch.toLowerCase());
                          })
                          .slice(0, 30)
                          .map(c => (
                            <button
                              key={c._id}
                              onClick={() => (linkPickerMode === 'parent' ? handleSetParent(c._id) : handleAddChild(c._id))}
                              className="w-full text-left text-xs px-2 py-1 hover:bg-white dark:hover:bg-gray-700 rounded"
                            >
                              {c.title} <span className="text-gray-400">· {c.column}</span>
                            </button>
                          ))}
                      </div>
                      <button
                        onClick={() => { setLinkPickerMode(null); setParentSearch(''); }}
                        className="text-[10px] text-gray-400 hover:underline mt-1"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {card.children && card.children.length > 0 ? (
                    <div className="space-y-1 ml-6">
                      {card.children.map(ch => (
                        <button
                          key={ch._id}
                          onClick={() => onNavigateToCard && onNavigateToCard(ch._id)}
                          className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 group/child"
                        >
                          <span className={`flex-shrink-0 w-3 h-3 rounded-full border ${ch.completedAt ? 'bg-green-500 border-green-500' : 'border-gray-300 dark:border-gray-500'}`} />
                          <span className={`flex-1 ${ch.completedAt ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-200'}`}>{ch.title}</span>
                          <span className="text-[10px] text-gray-400">{ch.column}</span>
                        </button>
                      ))}
                    </div>
                  ) : (!card.parent && !linkPickerMode && (
                    <p className="text-[11px] text-gray-400 ml-6">No linked cards. Set a parent or add a child card.</p>
                  ))}
                </div>

                {/* Subtasks */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2.5 flex items-center justify-between">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                      Subtasks
                    </span>
                    {card.subtasks && card.subtasks.length > 0 && (
                      <button
                        onClick={handlePromoteSubtasks}
                        className="text-[10px] px-3 py-1 bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 rounded-xl hover:bg-purple-200 dark:hover:bg-purple-900/40 font-bold normal-case tracking-normal border border-purple-200/20"
                        title="Turn each subtask into a real child card on the board"
                      >
                        Promote to Cards
                      </button>
                    )}
                  </h3>
                  <div className="space-y-2 ml-6">
                    {card.subtasks.map(st => (
                      <div key={st._id} className="flex items-center space-x-2 group/subtask">
                        <input
                          type="checkbox"
                          checked={st.completed}
                          onChange={() => toggleSubtask(st._id)}
                          className="rounded text-primary-blue focus:ring-primary-blue h-4 w-4"
                        />
                        {editingSubtaskId === st._id ? (
                          <>
                            <input
                              type="text"
                              autoFocus
                              value={editingSubtaskContent}
                              onChange={(e) => setEditingSubtaskContent(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveSubtask(st._id);
                                if (e.key === 'Escape') cancelEditSubtask();
                              }}
                              className="flex-1 text-sm border-gray-300 dark:border-gray-750 rounded-xl focus:ring-primary-blue focus:border-primary-blue px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            <button onClick={() => saveSubtask(st._id)} className="text-[10px] bg-primary-blue text-white px-3 py-1 rounded-xl font-bold shadow-sm">Save</button>
                            <button onClick={cancelEditSubtask} className="text-[10px] text-gray-500 hover:underline px-1">Cancel</button>
                          </>
                        ) : (
                          <>
                            <span
                              onClick={() => startEditSubtask(st)}
                              className={`flex-1 cursor-text ${st.completed ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-200'}`}
                              title="Click to edit"
                            >
                              {st.content}
                            </span>
                            <button
                              onClick={() => startEditSubtask(st)}
                              className="opacity-0 group-hover/subtask:opacity-100 text-gray-400 hover:text-primary-blue transition-opacity"
                              aria-label="Edit subtask"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            </button>
                            <button
                              onClick={() => deleteSubtask(st._id)}
                              className="opacity-0 group-hover/subtask:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"
                              aria-label="Delete subtask"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                    <form onSubmit={handleAddSubtask} className="mt-2.5">
                      <input 
                        type="text" 
                        value={subtask}
                        onChange={(e) => setSubtask(e.target.value)}
                        placeholder="Add a subtask..."
                        className="w-full text-sm border-gray-300 dark:border-gray-755 rounded-xl shadow-sm focus:ring-primary-blue focus:border-primary-blue bg-white dark:bg-gray-850 text-gray-900 dark:text-white p-2.5 placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </form>
                  </div>
                </div>

                {/* Timeline (Comments and Activity) */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                    Comments and Activity
                  </h3>
                  
                  <form onSubmit={handleAddComment} className="mb-6 ml-6">
                    <div className="flex items-start space-x-3">
                      <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                        {currentUser?.avatar ? (
                          <img src={getFileUrl(currentUser.avatar)} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center bg-secondary-cyan text-white text-xs">{currentUser?.name?.charAt(0).toUpperCase()}</div>
                        )}
                      </div>
                      <div className="flex-1 relative">
                        <textarea 
                          value={comment}
                          onChange={handleCommentChange}
                          onKeyDown={handleKeyDown}
                          placeholder="Write a comment... mention with @"
                          className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-primary-blue focus:border-primary-blue p-2"
                          rows="2"
                        ></textarea>
                        
                        {mentionActive && mentionSuggestions.length > 0 && (
                          <div className="absolute z-50 bottom-full left-0 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg mb-1 overflow-hidden">
                            {mentionSuggestions.map((user, index) => (
                              <div 
                                key={user._id}
                                className={`flex items-center space-x-2 p-2 cursor-pointer text-xs ${index === mentionIndex ? 'bg-primary-blue text-white' : 'hover:bg-gray-100'}`}
                                onClick={() => insertMention(user)}
                              >
                                <div className="h-5 w-5 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                                  {user.avatar ? (
                                    <img src={getFileUrl(user.avatar)} alt="" className="h-full w-full object-cover" />
                                  ) : (
                                    <div className="h-full w-full flex items-center justify-center bg-secondary-cyan text-white text-[8px]">{user.name?.charAt(0).toUpperCase() || '?'}</div>
                                  )}
                                </div>
                                <div className="flex-1 truncate">
                                  <span className="font-bold">{user.name}</span>
                                  {user.email && (
                                    <span className={`ml-2 text-[10px] ${index === mentionIndex ? 'text-blue-100' : 'text-gray-400'}`}>
                                      {user.email}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <button type="submit" className="mt-2 px-4 py-1.5 bg-primary-blue text-white text-xs font-bold rounded hover:bg-blue-700 transition-colors">Save</button>
                      </div>
                    </div>
                  </form>

                  <div className="space-y-6 ml-6">
                    {isProcessingAI && (
                      <div className="flex items-start space-x-3 animate-pulse">
                        <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xs font-bold">🤖</div>
                        <div className="flex-1 bg-purple-50 rounded-md p-3 border border-purple-100">
                          <p className="text-xs text-purple-700 font-bold italic">Agent is thinking and applying changes...</p>
                        </div>
                      </div>
                    )}
                    {/* Unified timeline: comments and activities interleaved by timestamp, newest first.
                        Activities of type 'comment' duplicate the comment array, so filter them out. */}
                    {(() => {
                      const items = [];
                      (card.comments || []).forEach((c) => items.push({ kind: 'comment', item: c, ts: new Date(c.createdAt).getTime() }));
                      (card.activities || []).forEach((a) => {
                        if (a.type !== 'comment') items.push({ kind: 'activity', item: a, ts: new Date(a.createdAt).getTime() });
                      });
                      items.sort((a, b) => b.ts - a.ts);
                      return items.map(({ kind, item }) => {
                        if (kind === 'activity') {
                          return (
                            <div key={`a-${item._id}`} className="flex items-center space-x-2 text-[11px] text-gray-500 dark:text-gray-400 pl-1 py-0.5">
                              <svg className="w-3 h-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              <span className="font-semibold text-gray-600 dark:text-gray-300">{item.user?.name || 'Someone'}</span>
                              <span className="flex-1">{item.text}</span>
                              {item.type === 'attachment' && item.filePath && isImage(item.filePath) && (
                                <div className="h-10 w-14 rounded border overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0" onClick={(e) => handlePreview(e, getFileUrl(item.filePath))}>
                                  <img src={getFileUrl(item.filePath)} alt="thumbnail" className="h-full w-full object-cover" />
                                </div>
                              )}
                              <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{new Date(item.createdAt).toLocaleString()}</span>
                            </div>
                          );
                        }
                        const c = item;
                        return (
                          <div key={c._id} className="flex items-start space-x-3 group/comment">
                            <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 mt-0.5">
                              {c.author?.avatar ? (
                                <img src={getFileUrl(c.author.avatar)} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center bg-secondary-cyan text-white text-xs">
                                  {c.author?.name?.charAt(0).toUpperCase() || '?'}
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{c.author?.name}</span>
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400">{new Date(c.createdAt).toLocaleString()}</span>
                                  {c.isEdited && <span className="text-[10px] text-gray-400 italic">(edited)</span>}
                                  {c.external?.source && <span className="text-[9px] font-bold uppercase tracking-wider text-[#00838d] dark:text-[#4fd0da]" title="Mirrored with the hub">via Sprock</span>}
                                </div>
                                {c.author?._id === currentUser?._id && !editingCommentId && (
                                  <div className="flex space-x-2 opacity-0 group-hover/comment:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => {
                                        setEditingCommentId(c._id);
                                        setEditedCommentContent(c.content);
                                      }}
                                      className="text-[10px] text-gray-400 hover:text-primary-blue font-bold"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteComment(c._id)}
                                      className="text-[10px] text-gray-400 hover:text-red-600 font-bold"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                              {editingCommentId === c._id ? (
                                <div className="mt-2 space-y-2">
                                  <textarea
                                    value={editedCommentContent}
                                    onChange={(e) => setEditedCommentContent(e.target.value)}
                                    className="w-full text-sm border-gray-300 rounded-md p-2 focus:ring-primary-blue"
                                    rows="2"
                                  />
                                  <div className="flex space-x-2">
                                    <button onClick={() => handleUpdateComment(c._id)} className="px-3 py-1 bg-primary-blue text-white text-[10px] font-bold rounded">Save</button>
                                    <button onClick={() => setEditingCommentId(null)} className="px-3 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded">Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div className="mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 shadow-sm relative">
                                  <MarkdownContent content={c.content} className="text-sm text-gray-700" />
                                  {c.history?.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                                      <button
                                        onClick={() => setShowHistoryId(showHistoryId === c._id ? null : c._id)}
                                        className="text-[10px] text-gray-400 hover:text-primary-blue flex items-center"
                                      >
                                        <svg className={`w-3 h-3 mr-1 transform transition-transform ${showHistoryId === c._id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        {showHistoryId === c._id ? 'Hide History' : `View History (${c.history.length})`}
                                      </button>
                                      {showHistoryId === c._id && (
                                        <div className="mt-2 space-y-3 bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-100 dark:border-gray-700 animate-fade-in">
                                          {c.history.map((h, idx) => (
                                            <div key={idx} className="text-[11px] text-gray-500 dark:text-gray-400">
                                              <div className="font-bold mb-1">Version {c.history.length - idx} • {new Date(h.editedAt).toLocaleString()}</div>
                                              <div className="italic">"{h.content}"</div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Assignees */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                    Assignees
                  </h3>
                  
                  <div className="flex items-center space-x-2 mb-3">
                    {/* Current Avatars */}
                    <div className="flex -space-x-2 overflow-hidden">
                      {card.assignedTo?.map(user => (
                        <div key={user._id} className="inline-block h-8 w-8 rounded-full ring-2 ring-white overflow-hidden bg-gray-200 shadow-sm flex-shrink-0" title={user.name}>
                          {user.avatar ? (
                            <img src={getFileUrl(user.avatar)} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center bg-secondary-cyan text-white text-[10px] font-black">{user.name?.charAt(0).toUpperCase()}</div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Add Button */}
                    <button
                      onClick={() => setShowAssigneeList(!showAssigneeList)}
                      className={`h-8 w-8 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-primary-blue hover:text-primary-blue transition-all ${showAssigneeList ? 'border-primary-blue text-primary-blue bg-primary-blue/5' : ''}`}
                      title="Add/Remove Assignees"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                    </button>
                  </div>
                  
                  {/* Toggleable Dropdown List */}
                  {showAssigneeList && (
                    <div className="mt-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 space-y-1 max-h-48 overflow-y-auto scrollbar-thin animate-fade-in shadow-inner">
                      {[currentUser, ...getCollaborators()]
                        .filter(u => u && u._id) // Ensure valid users
                        .sort((a, b) => {
                          const aAssigned = card.assignedTo?.some(u => (u._id || u) === a._id);
                          const bAssigned = card.assignedTo?.some(u => (u._id || u) === b._id);
                          if (aAssigned && !bAssigned) return -1;
                          if (!aAssigned && bAssigned) return 1;
                          const nameA = a.name || a.email || '';
                          const nameB = b.name || b.email || '';
                          return nameA.localeCompare(nameB);
                        })
                        .map(user => {
                          const isAssigned = card.assignedTo?.some(u => u._id === user._id);
                          return (
                            <button
                              key={user._id}
                              onClick={() => toggleAssignee(user._id)}
                              className={`w-full flex items-center justify-between p-2 rounded-lg transition-all ${isAssigned ? 'bg-white dark:bg-gray-700 shadow-sm ring-1 ring-primary-blue/30' : 'hover:bg-white/50 dark:hover:bg-gray-700/50'}`}
                            >
                              <div className="flex items-center space-x-3 min-w-0">
                                <div className="h-6 w-6 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-800 flex-shrink-0 shadow-sm border border-white dark:border-gray-800">
                                  {user.avatar ? (
                                    <img src={getFileUrl(user.avatar)} alt="" className="h-full w-full object-cover" />
                                  ) : (
                                    <div className="h-full w-full flex items-center justify-center bg-gray-300 dark:bg-gray-700 text-white text-[8px] font-black">{user.name?.charAt(0)}</div>
                                  )}
                                </div>
                                <span className={`text-xs truncate ${isAssigned ? 'font-bold text-primary-blue dark:text-blue-400' : 'text-gray-650 dark:text-gray-300'}`}>
                                  {user._id === currentUser._id ? 'Assign Me' : user.name}
                                </span>
                              </div>
                              {isAssigned && (
                                <div className="h-4 w-4 bg-primary-blue rounded-full flex items-center justify-center shadow-sm">
                                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7"></path></svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>

                {/* Due Date */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Due Date
                  </h3>
                  <input
                    type="date"
                    value={card.dueDate ? new Date(card.dueDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleDueDateChange(e.target.value)}
                    className="w-full text-sm border-gray-300 dark:border-gray-750 rounded-xl shadow-sm focus:ring-primary-blue focus:border-primary-blue bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2.5"
                  />
                </div>

                {/* Time Estimate */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Time Estimate
                  </h3>
                  <input
                    type="text"
                    value={estimateInput}
                    onChange={(e) => setEstimateInput(e.target.value)}
                    onBlur={commitEstimateInput}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="e.g. 30m, 1h 30m"
                    className="w-full text-sm border-gray-300 dark:border-gray-750 rounded-xl shadow-sm focus:ring-primary-blue focus:border-primary-blue bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2.5"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[15, 30, 60, 120, 240].map(m => (
                      <button
                        key={m}
                        onClick={() => handleEstimateChange(m)}
                        className={`text-[10px] px-2 py-1 rounded-lg font-bold border transition-colors ${card.timeEstimate === m ? 'bg-primary-blue text-white border-primary-blue' : 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-primary-blue hover:text-primary-blue'}`}
                      >
                        {formatEstimate(m)}
                      </button>
                    ))}
                    {card.timeEstimate ? (
                      <button
                        onClick={() => handleEstimateChange(null)}
                        className="text-[10px] px-2 py-1 rounded-lg font-bold text-gray-400 hover:text-red-500"
                        title="Clear estimate"
                      >
                        clear
                      </button>
                    ) : null}
                  </div>
                </div>

                {/* Recurrence */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                    Recurrence
                  </h3>
                  <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!card.recurrence?.enabled}
                      onChange={(e) => handleRecurrenceChange({
                        enabled: e.target.checked,
                        pattern: card.recurrence?.pattern || (e.target.checked ? 'weekly' : null),
                        intervalDays: card.recurrence?.intervalDays,
                        nextSpawnAt: card.recurrence?.nextSpawnAt
                      })}
                    />
                    <span className="text-gray-700 dark:text-gray-300">Repeat this card on a schedule</span>
                  </label>
                  {card.recurrence?.enabled && (
                    <div className="space-y-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Pattern</label>
                        <select
                          value={card.recurrence?.pattern || 'weekly'}
                          onChange={(e) => handleRecurrenceChange({
                            enabled: true,
                            pattern: e.target.value,
                            intervalDays: card.recurrence?.intervalDays,
                            nextSpawnAt: card.recurrence?.nextSpawnAt
                          })}
                          className="w-full text-xs border-gray-300 rounded focus:ring-primary-blue focus:border-primary-blue"
                        >
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Every 2 weeks</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="everyNDays">Every N days</option>
                        </select>
                      </div>
                      {card.recurrence?.pattern === 'everyNDays' && (
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Days between spawns</label>
                          <input
                            type="number"
                            min="1"
                            value={card.recurrence?.intervalDays || 7}
                            onChange={(e) => handleRecurrenceChange({
                              enabled: true,
                              pattern: 'everyNDays',
                              intervalDays: Number(e.target.value) || 7,
                              nextSpawnAt: card.recurrence?.nextSpawnAt
                            })}
                            className="w-full text-xs border-gray-300 rounded focus:ring-primary-blue focus:border-primary-blue"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Next spawn</label>
                        <input
                          type="date"
                          value={card.recurrence?.nextSpawnAt ? new Date(card.recurrence.nextSpawnAt).toISOString().split('T')[0] : ''}
                          onChange={(e) => handleRecurrenceChange({
                            enabled: true,
                            pattern: card.recurrence?.pattern || 'weekly',
                            intervalDays: card.recurrence?.intervalDays,
                            nextSpawnAt: e.target.value || null
                          })}
                          className="w-full text-xs border-gray-300 rounded focus:ring-primary-blue focus:border-primary-blue"
                        />
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                        This card acts as a template. A fresh copy is created on each spawn date. Comments and attachments do not carry over.
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions & Meta */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Actions</h3>
                  <div className="space-y-2">
                    <button 
                      onClick={() => {
                        setShowMoveOptions(!showMoveOptions);
                        if (!moveData.boardId) {
                          setMoveData({ boardId: card.board, column: card.column });
                        }
                      }}
                      className="w-full text-left px-3 py-2 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                      Move Card
                    </button>

                    {showMoveOptions && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 animate-fade-in space-y-3">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Target Board</label>
                          <select 
                            value={moveData.boardId}
                            onChange={(e) => {
                              const targetBoard = availableBoards.find(b => b._id === e.target.value);
                              setMoveData({ boardId: e.target.value, column: targetBoard?.columns[0] || '' });
                            }}
                            className="w-full text-xs border-gray-300 rounded focus:ring-primary-blue focus:border-primary-blue"
                          >
                            {availableBoards.map(b => (
                              <option key={b._id} value={b._id}>{b.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Target Column</label>
                          <select 
                            value={moveData.column}
                            onChange={(e) => setMoveData({ ...moveData, column: e.target.value })}
                            className="w-full text-xs border-gray-300 rounded focus:ring-primary-blue focus:border-primary-blue"
                          >
                            {availableBoards.find(b => b._id === moveData.boardId)?.columns.map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <button 
                          onClick={handleMoveCard}
                          className="w-full py-1.5 bg-primary-blue text-white text-xs font-bold rounded hover:bg-blue-700"
                        >
                          Confirm Move
                        </button>
                      </div>
                    )}

                    <button
                      onClick={async () => {
                        try {
                          const newVal = card.completedAt ? null : new Date().toISOString();
                          await axios.put(`/api/cards/${cardId}`, { completedAt: newVal }, {
                            headers: { 'x-auth-token': token }
                          });
                          fetchCardDetails();
                          onUpdate();
                        } catch (err) { console.error(err); }
                      }}
                      className={`w-full text-left px-3 py-2 rounded text-sm font-medium flex items-center ${
                        card.completedAt
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                      {card.completedAt ? 'Completed' : 'Mark Complete'}
                    </button>

                    <button
                      onClick={handleAddToDailyPlan}
                      className="w-full text-left px-3 py-2 bg-blue-50 text-primary-blue rounded text-sm font-medium hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/70 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      {planMsg || 'Add to Daily Plan'}
                    </button>

                    {card.share?.enabled ? (
                      <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-md border border-purple-200 dark:border-purple-900 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase tracking-widest text-purple-700 dark:text-purple-300">
                            Public link is on
                          </span>
                          {typeof card.share.viewCount === 'number' && (
                            <span className="text-[10px] text-purple-500">
                              {card.share.viewCount} view{card.share.viewCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                        <input
                          readOnly
                          value={shareUrl}
                          onFocus={(e) => e.target.select()}
                          className="w-full text-[11px] px-2 py-1.5 rounded border border-purple-200 dark:border-purple-900 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={copyShareUrl}
                            className="flex-1 py-1.5 bg-purple-600 text-white text-xs font-bold rounded hover:bg-purple-700"
                          >
                            {shareCopied ? 'Copied' : 'Copy link'}
                          </button>
                          <button
                            onClick={handleRevokeShare}
                            disabled={shareBusy}
                            className="px-3 py-1.5 text-xs font-bold text-purple-700 dark:text-purple-300 rounded hover:bg-purple-100 dark:hover:bg-purple-900/40 disabled:opacity-50"
                          >
                            Turn off
                          </button>
                        </div>
                        <p className="text-[10px] text-purple-600 dark:text-purple-400 leading-tight">
                          Unlisted, not password protected. Shows the description and every attached file.
                        </p>
                      </div>
                    ) : (
                      <button
                        onClick={handleCreateShare}
                        disabled={shareBusy}
                        className="w-full text-left px-3 py-2 bg-purple-50 text-purple-700 rounded text-sm font-medium hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:hover:bg-purple-950/70 flex items-center disabled:opacity-50"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m6.5-6.5l1.5-1.5a4 4 0 115.656 5.656l-3 3a4 4 0 01-5.656 0" /></svg>
                        {shareBusy ? 'Creating link…' : 'Share link (files included)'}
                      </button>
                    )}

                    <button
                      onClick={handleArchiveCard}
                      className="w-full text-left px-3 py-2 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                      Archive Card
                    </button>

                    <button 
                      onClick={handleDeleteCardPermanent}
                      className="w-full text-left px-3 py-2 bg-red-50 text-red-700 rounded text-sm font-medium hover:bg-red-100 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Delete Card
                    </button>

                    <button 
                      onClick={handleSubscription}
                      className={`w-full text-left px-3 py-2 rounded text-sm font-medium flex items-center ${isSubscribed ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-secondary-cyan text-white hover:bg-cyan-600'}`}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                      {isSubscribed ? 'Unsubscribe' : 'Subscribe'}
                    </button>
                  </div>
                  <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                    {isSubscribed ? 'You are receiving email notifications.' : 'Get notified about changes.'}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Attachments</h3>
                  <div className="space-y-2 mb-3">
                    {card.attachments.map(a => {
                      const image = isImage(a.path);
                      return (
                        <div key={a._id} className="flex items-center bg-gray-50 dark:bg-gray-800 p-2 rounded group mb-1">
                          <div className="flex-1 min-w-0 mr-3">
                            <a
                              href={a.signedUrl || getFileUrl(a.path)}
                              onClick={(e) => { e.preventDefault(); setShowFileExplorer(true); }}
                              className="text-primary-blue hover:underline font-bold truncate block"
                              title={a.name || 'Unnamed File'}
                            >
                              {a.name || 'Unnamed File'}
                            </a>
                          </div>
                          
                          <div className="flex items-center space-x-1.5 flex-shrink-0">
                            {image && (
                              <button
                                onClick={() => handleSetCover(a.path)}
                                className="p-1 text-gray-400 hover:text-primary-blue transition-colors"
                                title="Make Cover"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <label className={`block w-full text-center px-3 py-2 border-2 border-dashed rounded-md cursor-pointer hover:border-primary-blue transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <span className="text-xs text-gray-600">{uploading ? `Uploading... ${uploadPercent}%` : 'Upload File (up to 1 GB)'}</span>
                    <input type="file" onChange={handleFileUpload} disabled={uploading} className="hidden" />
                  </label>
                </div>

                {card.external?.source && (
                  <div className="p-3 rounded-md border border-[#00A5B1]/40 bg-[#00A5B1]/5 space-y-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#00838d] dark:text-[#4fd0da]">
                      Mirrored from Sprock
                    </p>
                    {(card.external.board || card.external.column) && (
                      <p className="text-xs text-gray-700 dark:text-gray-200">
                        {card.external.board}{card.external.column ? ` › ${card.external.column}` : ''}
                      </p>
                    )}
                    {card.external.status === 'completed' && (
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">Completed in the hub.</p>
                    )}
                    {card.external.status === 'gone' && (
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">No longer assigned to you in the hub.</p>
                    )}
                    {card.external.url && (
                      <a
                        href={card.external.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-bold text-[#00838d] dark:text-[#4fd0da] hover:underline"
                      >
                        Open in Sprock
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                      </a>
                    )}
                    {card.external.syncedAt && (
                      <p className="text-[10px] text-gray-400">Synced {new Date(card.external.syncedAt).toLocaleString()}</p>
                    )}
                  </div>
                )}

                <div className="pt-4 border-t">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 border border-gray-200 dark:border-gray-600 shadow-sm">
                      {card.createdBy?.avatar ? (
                        <img src={getFileUrl(card.createdBy.avatar)} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 text-[10px] font-black uppercase">
                          {card.createdBy?.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                      <p className="font-black uppercase tracking-tighter text-gray-900 dark:text-gray-100">Created by: {card.createdBy?.name}</p>
                      <p>on {new Date(card.createdAt).toLocaleDateString()}</p>
                      <p className="mt-0.5">Priority: <span className="font-bold text-gray-700">{card.priority}</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 flex justify-end">
              <button 
                onClick={onClose}
                className="bg-gray-900 text-white px-8 py-2.5 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-black transition-all shadow-lg hover:shadow-black/20"
              >
                Save & Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Attachment File Explorer */}
      {showFileExplorer && card && (
        <AttachmentExplorer
          card={card}
          token={token}
          onClose={() => setShowFileExplorer(false)}
          onPreview={(att) => handleOpenPreview(null, att)}
          onCardUpdated={() => fetchCardDetails()}
        />
      )}

      {/* File Preview Lightbox */}
      {previewFile && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-black bg-opacity-95 transition-opacity">
          {/* Header Action Bar */}
          <div className="p-4 bg-black/50 backdrop-blur-md flex justify-between items-center z-[70] border-b border-white/10">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setPreviewFile(null)}
                className="text-white hover:text-gray-300 p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
              <div className="min-w-0">
                <p className="text-white text-sm font-bold truncate">{activeAttachment?.name}</p>
                <p className="text-gray-400 text-[10px] uppercase font-black tracking-widest">{previewFile.type} Preview</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button 
                onClick={() => {
                  handleReviewAttachment(activeAttachment);
                  setPreviewFile(null);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-purple-700 transition-all flex items-center shadow-lg"
              >
                <span className="mr-2">🤖</span> AI Review
              </button>
              <a 
                href={previewFile.url} 
                target="_blank" 
                rel="noopener noreferrer"
                download={activeAttachment?.name}
                className="px-4 py-2 bg-white/10 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all border border-white/10"
              >
                Download
              </a>
              <button 
                onClick={() => {
                  handleDeleteAttachment(activeAttachment._id);
                  setPreviewFile(null);
                }}
                className="px-4 py-2 bg-red-600/20 text-red-400 border border-red-600/30 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all"
              >
                Delete
              </button>
            </div>
          </div>
          
          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden" onClick={() => setPreviewFile(null)}>
            <div className="w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              {previewFile.type === 'image' ? (
                <img 
                  src={previewFile.url} 
                  alt="Preview" 
                  className="max-w-full max-h-full object-contain shadow-2xl"
                />
              ) : (
                <iframe 
                  src={previewFile.url} 
                  title="PDF Preview"
                  className="w-full h-full max-w-5xl rounded-lg shadow-2xl bg-white dark:bg-gray-800"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {isPreviewModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase tracking-widest text-gray-900 dark:text-gray-100">{activeAttachment?.name}</h3>
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-tighter">Document Preview</p>
                </div>
              </div>
              <button onClick={() => setIsPreviewModalOpen(false)} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-8 font-serif leading-relaxed text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900">
              <pre className="whitespace-pre-wrap font-sans text-sm">{previewText}</pre>
            </div>
            <div className="p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 flex justify-between items-center">
              <div className="flex space-x-2">
                <button 
                  onClick={() => {
                    handleReviewAttachment(activeAttachment);
                    setIsPreviewModalOpen(false);
                  }}
                  className="bg-purple-600 text-white px-4 py-2 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-purple-700 transition-all flex items-center shadow-md"
                >
                  <span className="mr-2">🤖</span> AI Review
                </button>
                <a 
                  href={activeAttachment?.signedUrl || getFileUrl(activeAttachment?.path)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  download={activeAttachment?.name}
                  className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-gray-50 dark:hover:bg-gray-600 transition-all"
                >
                  Download
                </a>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => {
                    handleDeleteAttachment(activeAttachment._id);
                    setIsPreviewModalOpen(false);
                  }}
                  className="text-red-500 px-4 py-2 font-black uppercase text-xs tracking-widest hover:bg-red-50 rounded-xl transition-all"
                >
                  Delete File
                </button>
                <button onClick={() => setIsPreviewModalOpen(false)} className="bg-gray-900 text-white px-6 py-2 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-black transition-all shadow-lg">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardModal;